import { useEffect, useState } from 'react';

import apiClient from 'services/api';
import { estaPausado, temPerfilProfissional } from 'services/perfilProfissionalStatus';

// Cache em memória por sessão do app (reseta só num F5) — evita refazer a chamada a
// cada remontagem de componente e, junto com "emAndamento", evita duas telas (ex.:
// NavBar e Dashboard) dispararem a mesma requisição em paralelo.
//
// Guarda a RESPOSTA de GET /usuario/{id} ({ usuario, profissional }), não só o papel derivado dela:
// a mesma chamada alimenta dois consumidores — o papel (usePerfilProfissional) e a foto de perfil
// do usuário logado (useAvatarUsuario). Cachear só o booleano obrigaria o segundo a repetir a
// requisição que o NavBar acabou de fazer.
const cache = new Map();
const emAndamento = new Map();

// Quem quer saber quando o cache muda (ver useAvatarUsuario). Um Set de funções e não um context:
// o hook é usado solto no NavBar, na BarraInferior e no Meu cadastro, e um Provider obrigaria
// todos a mudar para resolver o que três linhas resolvem.
const ouvintes = new Set();

export function assinarCadastroLogado(ouvinte) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

function notificar() {
  ouvintes.forEach((ouvinte) => ouvinte());
}

/** O cadastro cacheado do usuário, ou undefined enquanto não resolveu. Síncrono, para snapshot. */
export function lerCadastroCacheado(userId) {
  return cache.get(userId);
}

export function buscarCadastroLogado(userId) {
  if (cache.has(userId)) {
    return Promise.resolve(cache.get(userId));
  }

  if (!emAndamento.has(userId)) {
    const promise = apiClient
      .get(`/usuario/${userId}`)
      .then((response) => {
        const dados = {
          usuario: response?.data?.usuario || null,
          profissional: response?.data?.profissional || null,
        };
        cache.set(userId, dados);
        notificar();
        return dados;
      })
      .finally(() => {
        emAndamento.delete(userId);
      });
    emAndamento.set(userId, promise);
  }

  return emAndamento.get(userId);
}

/**
 * Atualiza só a foto no cadastro cacheado e avisa quem está ouvindo — sem refetch.
 *
 * É o que faz o NavBar (e a barra inferior do app) trocar a foto no instante em que o Meu cadastro
 * termina o envio, em vez de esperar um F5. Quem chama já tem o nome novo na mão (veio na resposta
 * do upload); repetir GET /usuario/{id} só para ler de volta o que se acabou de gravar seria uma
 * requisição a mais no fim de uma ação que a pessoa já está esperando.
 */
export function definirAvatarUsuario(userId, avatar) {
  const atual = cache.get(userId);
  if (!atual) return;
  cache.set(userId, { ...atual, usuario: { ...(atual.usuario || {}), avatar: avatar || null } });
  notificar();
}

// Não é `!!dados.profissional`: a linha em `profissionais` não some quando o perfil é desligado,
// ela vai para INATIVO. Com a checagem antiga, quem desativava continuava vendo o Dashboard de
// profissional e perdia o "Buscar profissional" do menu. A regra é a mesma que o Meu cadastro
// aplica — por isso mora num módulo só.
const papelDe = (dados) => temPerfilProfissional(dados?.profissional);

/**
 * Esquece o cadastro guardado para o usuário (ou para todos, sem argumento).
 *
 * Existe por causa do cache acima: ativar ou desativar o perfil muda o papel no meio da sessão, e
 * sem isto o NavBar e o Dashboard seguiriam com o valor antigo até um F5 — justamente o momento em
 * que precisam mudar. Chamada por quem executa a transição, não pelo hook.
 */
export function invalidarPerfilProfissional(userId) {
  if (userId === undefined) {
    cache.clear();
    emAndamento.clear();
  } else {
    cache.delete(userId);
    emAndamento.delete(userId);
  }
  notificar();
}

// Mesma resposta, outra pergunta: o perfil está fora da busca por pausa (férias, folga)?
const pausaDe = (dados) => estaPausado(dados?.profissional);

/**
 * Um valor derivado do cadastro cacheado. null enquanto não resolvido. `derivar` precisa ser estável
 * (função de módulo): é dependência do efeito.
 */
function useDerivadoDoCadastro(userId, derivar) {
  const [valor, setValor] = useState(() => (
    userId && cache.has(userId) ? derivar(cache.get(userId)) : null
  ));

  useEffect(() => {
    if (!userId) {
      setValor(null);
      return undefined;
    }

    if (cache.has(userId)) {
      setValor(derivar(cache.get(userId)));
      return undefined;
    }

    let ativo = true;
    buscarCadastroLogado(userId)
      .then((dados) => {
        if (ativo) setValor(derivar(dados));
      })
      .catch(() => { /* silencioso: quem consome trata o null como "ainda não sei" */ });

    return () => { ativo = false; };
  }, [userId, derivar]);

  return valor;
}

/**
 * Indica se o usuário logado tem perfil de profissional (GET /usuario/{userId}).
 * Retorna null enquanto não resolvido — quem consome decide o que fazer nesse meio-tempo
 * (ex.: NavBar esconde o item de menu até saber o valor certo, em vez de mostrar um
 * rótulo que pode estar errado).
 */
export function usePerfilProfissional(userId) {
  return useDerivadoDoCadastro(userId, papelDe);
}

/**
 * Indica se o perfil profissional do usuário logado está em pausa. Mesma chamada e mesmo cache do
 * papel, sem requisição a mais — é o que acende o lembrete do Dashboard.
 */
export function usePerfilPausado(userId) {
  return useDerivadoDoCadastro(userId, pausaDe);
}
